/**
 * Copyright (c) Microsoft. All rights reserved.
 */

/**
 * Searches an array of strings using the index of with logic; finds the
 * strings that have the needle as a sub string.
 *
 * This function can be reduced in logic when we can induce T[K] type
 * as a string.
 *
 * @param haystack The items to search.
 * @param needle The query to search with.
 * @param keyParams An optional keys params if T was an object.
 * @param search An optional special search function.
 * @returns The items where the search key parameter has the needle as a substring.
 */
export const substringSearchStrategy = <T, K extends keyof T>(
    haystack: T[],
    needle: string,
    keyParams?: K | K[],
    searchFn?: (item: T, query: string) => boolean
): T[] => {
    const query = (needle || '').toLocaleLowerCase();

    return haystack.filter(hay => {
        if (searchFn) {
            return searchFn(hay, query);
        }

        const value = keyParams
            ? typeof keyParams === 'string'
                ? hay[keyParams]
                : keyParams.reduce((acc, curr) => `${acc} ${hay[curr]}`, '')
            : hay;

        if (typeof value === 'string') {
            return value.toLocaleLowerCase().indexOf(query) !== -1;
        }

        return false;
    });
};
